import { useState } from "react";
import { Link } from "react-router-dom";
import { generateClient } from "aws-amplify/data";
import type { Schema } from "amplify/data/resource";
import toast from "react-hot-toast";
import { Loader2 } from "lucide-react";
import {
  parseTracks,
  formatEpoch,
  type BusinessProfile,
  type MerchantLifecycle,
  type TrackState,
} from "../types";
import { Checkbox } from "@/components/ui/base/checkbox";
import { Label } from "@/components/ui/base/label";
import { IdentityStageBadge, type IdentityStage } from "./IdentityStageBadge";

const client = generateClient<Schema>();

interface IdentityFailure {
  reason: string;
  message?: string;
  attemptCount: number;
  canRetry: boolean;
}

interface IdentityTrackMetadata {
  identityHash?: string;
  verificationSessionIds?: string[];
  collidingBusinessIds?: string[];
  lastAttemptedAt?: number;
  unrecoverableReason?:
    | "verified_outputs_missing"
    | "session_not_found"
    | "incomplete_pii"
    | string;
  unrecoverableDetectedAt?: number;
  lastFailure?: IdentityFailure;
}

const VALID_STAGES: readonly IdentityStage[] = [
  "not_verified",
  "pending_backfill",
  "verified",
  "duplicate",
  "unrecoverable",
  "failed",
];

function asIdentityStage(value: unknown): IdentityStage {
  if (
    typeof value === "string" &&
    VALID_STAGES.includes(value as IdentityStage)
  ) {
    return value as IdentityStage;
  }
  return "not_verified";
}

function getIdentityTrack(
  lifecycle: MerchantLifecycle | null,
): TrackState | undefined {
  if (!lifecycle) return undefined;
  const tracks = parseTracks(lifecycle.tracks);
  return tracks.identity;
}

function formatUnrecoverableReason(reason: string | null | undefined): string {
  switch (reason) {
    case "verified_outputs_missing":
      return "Stripe verification session has no recoverable PII payload";
    case "session_not_found":
      return "Verification session is no longer retrievable from Stripe";
    case "incomplete_pii":
      return "Verification session returned PII but key fields are missing";
    default:
      return reason ?? "Unknown reason";
  }
}

function formatTimestampMs(ms: number | undefined | null): string {
  if (!ms) return "—";
  return new Date(ms).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function formatHashCapturedAt(
  metadata: IdentityTrackMetadata,
  business: BusinessProfile,
): string {
  if (metadata.lastAttemptedAt) {
    return formatTimestampMs(metadata.lastAttemptedAt * 1000);
  }
  if (business.identityVerificationCompletedAt) {
    return formatEpoch(business.identityVerificationCompletedAt);
  }
  return "—";
}

interface Props {
  business: BusinessProfile;
  lifecycle: MerchantLifecycle | null;
  onRefresh: () => void | Promise<void>;
}

export function LifecycleIdentitySection({
  business,
  lifecycle,
  onRefresh,
}: Props) {
  const [retrying, setRetrying] = useState(false);
  const [allowFromUnrecoverable, setAllowFromUnrecoverable] = useState(false);

  const track = getIdentityTrack(lifecycle);
  const stage: IdentityStage = track
    ? asIdentityStage(track.stage)
    : "not_verified";
  const metadata = (track?.metadata ?? {}) as IdentityTrackMetadata;

  async function handleRetry() {
    setRetrying(true);
    try {
      const result = await client.mutations.backfillSingleIdentityDedupe({
        input: {
          businessId: business.id,
          allowFromUnrecoverable:
            stage === "unrecoverable" ? allowFromUnrecoverable : undefined,
        },
      });
      if (result.errors?.length) {
        toast.error(`Retry failed: ${result.errors[0].message}`);
        return;
      }
      const data = result.data;
      if (!data) {
        toast.error("Retry returned no data");
        return;
      }
      const outcomeMessage = (() => {
        switch (data.outcome) {
          case "verified":
            return "Hash captured — verified";
          case "duplicate":
            return "Hash captured — duplicate identity detected";
          case "unrecoverable":
            return `Unrecoverable: ${data.reason}`;
          case "failed":
            return `Failed: ${data.reason}`;
          default:
            return "Backfill complete";
        }
      })();
      if (data.outcome === "verified" || data.outcome === "duplicate") {
        toast.success(outcomeMessage);
      } else {
        toast.error(outcomeMessage);
      }
      await onRefresh();
    } finally {
      setRetrying(false);
    }
  }

  const showRetryButton = stage === "failed" || stage === "unrecoverable";

  return (
    <div className="border-border mt-6 border-t pt-5">
      <h3 className="text-muted-foreground mb-3 text-xs font-semibold tracking-wide uppercase">
        Identity Verification
      </h3>
      <div className="space-y-3">
        <div>
          <IdentityStageBadge stage={stage} size="md" />
        </div>

        {stage === "not_verified" && (
          <p className="text-muted-foreground text-sm">IDV not started</p>
        )}

        {stage === "pending_backfill" && (
          <p className="text-muted-foreground text-sm">
            Awaiting backfill — verified on Stripe before dedup feature shipped
          </p>
        )}

        {stage === "verified" && (
          <p className="text-foreground text-sm">
            Hash captured {formatHashCapturedAt(metadata, business)}
          </p>
        )}

        {stage === "duplicate" && (
          <>
            <p className="text-foreground text-sm">
              Hash captured {formatHashCapturedAt(metadata, business)}
            </p>
            {metadata.collidingBusinessIds &&
              metadata.collidingBusinessIds.length > 0 && (
                <div className="mt-3">
                  <p className="text-muted-foreground mb-1 text-xs">
                    Shares identity with:
                  </p>
                  <ul className="space-y-1">
                    {metadata.collidingBusinessIds.map((id) => (
                      <li key={id} className="text-sm">
                        <Link
                          to={`/merchants/${id}`}
                          className="text-blue-600 hover:underline"
                        >
                          {id}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
          </>
        )}

        {stage === "unrecoverable" && (
          <p className="text-foreground text-sm">
            {formatUnrecoverableReason(metadata.unrecoverableReason)}
          </p>
        )}

        {stage === "failed" && metadata.lastFailure && (
          <p className="text-foreground text-sm">
            {metadata.lastFailure.reason} (attempt{" "}
            {metadata.lastFailure.attemptCount}):{" "}
            {metadata.lastFailure.message ?? "—"}
          </p>
        )}

        {showRetryButton && (
          <div className="space-y-2">
            {stage === "unrecoverable" && (
              <div className="flex items-start gap-2">
                <Checkbox
                  id={`allow-unrecoverable-${business.id}`}
                  checked={allowFromUnrecoverable}
                  onCheckedChange={(checked) =>
                    setAllowFromUnrecoverable(checked === true)
                  }
                  disabled={retrying}
                />
                <Label
                  htmlFor={`allow-unrecoverable-${business.id}`}
                  className="text-foreground text-xs leading-tight"
                >
                  Allow rerun from unrecoverable
                  <span className="text-muted-foreground block font-normal">
                    Only use if Stripe now has recoverable PII for this session.
                  </span>
                </Label>
              </div>
            )}
            <button
              type="button"
              onClick={handleRetry}
              disabled={retrying}
              className="disabled:bg-muted disabled:text-muted-foreground inline-flex items-center gap-2 rounded-md bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-blue-700 disabled:cursor-not-allowed"
            >
              {retrying && <Loader2 className="h-3 w-3 animate-spin" />}
              Retry backfill
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
